import { useState, useEffect } from "react";

export const ENABLE_INACTIVITY_TIMEOUT = true;
export const INACTIVITY_LIMIT = 5 * 60 * 60 * 1000; // 5 hours in ms

export const DEFAULT_BOT_MESSAGE = {
    text: "👋 Hello! Welcome to Selsmart Support — I'm your Selsmart Buddy. How can I help you today?",
    role: "bot",
    ts: new Date().toISOString()
};

const STORAGE_KEYS = {
    MESSAGES: "selsmart_chat_messages",
    SUGGESTIONS: "selsmart_chat_suggestions",
    SELSMART_TOKEN: "selsmart_token",
    X_SESSION_TOKEN: "x_session_token",
    LAST_ACTIVITY: "selsmart_last_activity"
};

export const getInactivityStatus = () => {
    if (!ENABLE_INACTIVITY_TIMEOUT) return false;
    const lastActivity = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
    if (lastActivity) {
        const timeDiff = Date.now() - parseInt(lastActivity);
        return timeDiff > INACTIVITY_LIMIT;
    }
    return false;
};

export const updateActivity = () => {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
};

export function useChatSession() {
    // ── 1. State Initializers ────────────────────────────────────────────────
    const [messages, setMessages] = useState<{ text: string; role: string, ts: string }[]>(() => {
        const isInactive = getInactivityStatus();
        const saved = isInactive ? null : localStorage.getItem(STORAGE_KEYS.MESSAGES);
        return saved ? JSON.parse(saved) : [DEFAULT_BOT_MESSAGE];
    });

    const [suggestions, setSuggestions] = useState<[string, string, boolean][]>(() => {
        const isInactive = getInactivityStatus();
        const saved = isInactive ? null : localStorage.getItem(STORAGE_KEYS.SUGGESTIONS);
        return saved ? JSON.parse(saved) : [["", "", false]];
    });

    const [selsmartToken, setSelsmartToken] = useState<string>(() => {
        const isInactive = getInactivityStatus();
        return isInactive ? "" : (localStorage.getItem(STORAGE_KEYS.SELSMART_TOKEN) || "");
    });

    const [xSessionToken, setXSessionToken] = useState<string>(() => {
        const isInactive = getInactivityStatus();
        return isInactive ? "" : (localStorage.getItem(STORAGE_KEYS.X_SESSION_TOKEN) || "");
    });

    // ── 2. Persistence Synchronization ────────────────────────────────────────
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.MESSAGES, JSON.stringify(messages));
    }, [messages]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.SUGGESTIONS, JSON.stringify(suggestions));
    }, [suggestions]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.SELSMART_TOKEN, selsmartToken);
    }, [selsmartToken]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.X_SESSION_TOKEN, xSessionToken);
    }, [xSessionToken]);

    // ── 3. Cross-Tab Sync ─────────────────────────────────────────────────────
    useEffect(() => {
        const handleStorageUpdate = (e: StorageEvent) => {
            if (e.key === STORAGE_KEYS.MESSAGES && e.newValue) {
                setMessages(JSON.parse(e.newValue));
            }
            if (e.key === STORAGE_KEYS.SUGGESTIONS && e.newValue) {
                setSuggestions(JSON.parse(e.newValue));
            }
            if (e.key === STORAGE_KEYS.SELSMART_TOKEN && e.newValue !== null) {
                setSelsmartToken(e.newValue);
            }
            if (e.key === STORAGE_KEYS.X_SESSION_TOKEN && e.newValue !== null) {
                setXSessionToken(e.newValue);
            }
        };

        window.addEventListener("storage", handleStorageUpdate);
        return () => window.removeEventListener("storage", handleStorageUpdate);
    }, []);

    const resetSession = () => {
        setMessages([DEFAULT_BOT_MESSAGE]);
        setSuggestions([["", "", false]]);
        setSelsmartToken("");
        setXSessionToken("");
        updateActivity();
    };

    return {
        messages,
        setMessages,
        suggestions,
        setSuggestions,
        selsmartToken,
        setSelsmartToken,
        xSessionToken,
        setXSessionToken,
        getInactivityStatus,
        updateActivity,
        resetSession
    };
}
